import { useState } from 'react';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const CHATWOOT_CLOUD_URL = 'https://app.chatwoot.com';

export type InstanceBotOption = {
  instanceBotId: string;
  label: string;
};

export type ChatwootChannelFormValues = {
  instanceBotId: string;
  name: string;
  baseUrl: string;
  accountId: string;
  inboxId: string;
  apiAccessToken: string;
  hmacSignatureKey: string;
  agentBotIdentifier: string;
  agentBotApiKey: string;
  handoffOnAssignment: boolean;
  handoffKeyword: string;
  resumeKeyword: string;
  idleHandoffMinutes: number;
};

const formSchema = z.object({
  instanceBotId: z.string().uuid(),
  name: z.string().min(1).max(255),
  baseUrl: z.string().url().startsWith('https://').max(500),
  accountId: z.string().min(1).max(64),
  inboxId: z.string().min(1).max(64),
  apiAccessToken: z.string().min(8).max(512),
});

type ChatwootChannelFormProps = {
  instanceBotOptions: InstanceBotOption[];
  submitting: boolean;
  onSubmit: (values: ChatwootChannelFormValues) => void;
  onCancel: () => void;
};

export function ChatwootChannelForm({
  instanceBotOptions,
  submitting,
  onSubmit,
  onCancel,
}: ChatwootChannelFormProps) {
  const [selfHosted, setSelfHosted] = useState(false);
  const [showAgentBot, setShowAgentBot] = useState(false);
  const [form, setForm] = useState<ChatwootChannelFormValues>({
    instanceBotId: instanceBotOptions[0]?.instanceBotId ?? '',
    name: '',
    baseUrl: CHATWOOT_CLOUD_URL,
    accountId: '',
    inboxId: '',
    apiAccessToken: '',
    hmacSignatureKey: '',
    agentBotIdentifier: '',
    agentBotApiKey: '',
    handoffOnAssignment: true,
    handoffKeyword: '',
    resumeKeyword: '',
    idleHandoffMinutes: 0,
  });

  const effectiveBaseUrl = selfHosted ? form.baseUrl : CHATWOOT_CLOUD_URL;

  const handleSubmit = () => {
    const parsed = formSchema.safeParse({
      ...form,
      name: form.name.trim(),
      baseUrl: effectiveBaseUrl.trim(),
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      toast.error(issue?.message ?? 'Revisá los campos del formulario');
      return;
    }
    onSubmit({
      ...form,
      name: form.name.trim(),
      baseUrl: effectiveBaseUrl.trim().replace(/\/$/, ''),
      accountId: form.accountId.trim(),
      inboxId: form.inboxId.trim(),
      apiAccessToken: form.apiAccessToken.trim(),
      hmacSignatureKey: form.hmacSignatureKey.trim(),
      agentBotIdentifier: form.agentBotIdentifier.trim(),
      agentBotApiKey: form.agentBotApiKey.trim(),
      handoffKeyword: form.handoffKeyword.trim(),
      resumeKeyword: form.resumeKeyword.trim(),
    });
  };

  return (
    <div className='space-y-4'>
      <div>
        <Label>Bot / instancia *</Label>
        <select
          className='w-full border border-input rounded-md p-2 bg-background mt-1'
          value={form.instanceBotId}
          onChange={e => setForm({ ...form, instanceBotId: e.target.value })}
          disabled={instanceBotOptions.length === 0}
        >
          {instanceBotOptions.length === 0 ? (
            <option value=''>No hay bots disponibles sin canal Chatwoot</option>
          ) : (
            instanceBotOptions.map(option => (
              <option key={option.instanceBotId} value={option.instanceBotId}>
                {option.label}
              </option>
            ))
          )}
        </select>
      </div>

      <div>
        <Label>Nombre del canal *</Label>
        <Input
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder='Soporte Chatwoot'
        />
      </div>

      <div className='flex items-center justify-between rounded-lg border p-3'>
        <div>
          <Label className='font-medium'>Instancia self-hosted</Label>
          <p className='text-xs text-muted-foreground mt-1'>
            Desactivado usa Chatwoot Cloud ({CHATWOOT_CLOUD_URL}).
          </p>
        </div>
        <Switch
          checked={selfHosted}
          onCheckedChange={checked => {
            setSelfHosted(checked);
            if (!checked) {
              setForm(current => ({ ...current, baseUrl: CHATWOOT_CLOUD_URL }));
            }
          }}
        />
      </div>

      {selfHosted && (
        <div>
          <Label>Base URL (HTTPS) *</Label>
          <Input
            value={form.baseUrl}
            onChange={e => setForm({ ...form, baseUrl: e.target.value })}
            placeholder='https://chatwoot.miempresa.com'
          />
        </div>
      )}

      <div className='grid gap-4 md:grid-cols-2'>
        <div>
          <Label>Account ID *</Label>
          <Input
            value={form.accountId}
            onChange={e => setForm({ ...form, accountId: e.target.value })}
            placeholder='12'
          />
        </div>
        <div>
          <Label>Inbox ID *</Label>
          <Input
            value={form.inboxId}
            onChange={e => setForm({ ...form, inboxId: e.target.value })}
            placeholder='7'
          />
        </div>
      </div>

      <div>
        <Label>API access token *</Label>
        <Input
          type='password'
          value={form.apiAccessToken}
          onChange={e => setForm({ ...form, apiAccessToken: e.target.value })}
          autoComplete='new-password'
        />
      </div>

      <div>
        <Label>HMAC signature key (recomendado)</Label>
        <Input
          type='password'
          value={form.hmacSignatureKey}
          onChange={e => setForm({ ...form, hmacSignatureKey: e.target.value })}
          placeholder='Secreto configurado en el inbox de Chatwoot'
          autoComplete='new-password'
        />
      </div>

      <div className='flex items-center justify-between rounded-lg border p-3'>
        <Label className='font-medium'>Integración Agent Bot (opcional)</Label>
        <Switch checked={showAgentBot} onCheckedChange={setShowAgentBot} />
      </div>

      {showAgentBot && (
        <div className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label>Agent bot identifier</Label>
            <Input
              value={form.agentBotIdentifier}
              onChange={e => setForm({ ...form, agentBotIdentifier: e.target.value })}
            />
          </div>
          <div>
            <Label>Agent bot API key</Label>
            <Input
              type='password'
              value={form.agentBotApiKey}
              onChange={e => setForm({ ...form, agentBotApiKey: e.target.value })}
              autoComplete='new-password'
            />
          </div>
        </div>
      )}

      <div className='rounded-lg border p-3 space-y-3'>
        <p className='text-sm font-medium'>Handoff bot ↔ humano</p>
        <div className='flex items-center justify-between'>
          <Label>Handoff al asignar agente</Label>
          <Switch
            checked={form.handoffOnAssignment}
            onCheckedChange={checked => setForm({ ...form, handoffOnAssignment: checked })}
          />
        </div>
        <div className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label>Keyword handoff</Label>
            <Input
              value={form.handoffKeyword}
              onChange={e => setForm({ ...form, handoffKeyword: e.target.value })}
              placeholder='humano'
            />
          </div>
          <div>
            <Label>Keyword resume bot</Label>
            <Input
              value={form.resumeKeyword}
              onChange={e => setForm({ ...form, resumeKeyword: e.target.value })}
              placeholder='bot'
            />
          </div>
        </div>
        <div>
          <Label>Idle handoff (minutos, 0 = desactivado)</Label>
          <Input
            type='number'
            min={0}
            max={10080}
            value={form.idleHandoffMinutes}
            onChange={e =>
              setForm({ ...form, idleHandoffMinutes: Number.parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
      </div>

      <div className='flex justify-end gap-2'>
        <Button type='button' variant='outline' onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type='button'
          disabled={submitting || instanceBotOptions.length === 0}
          onClick={handleSubmit}
        >
          {submitting ? 'Creando…' : 'Crear canal'}
        </Button>
      </div>
    </div>
  );
}
